import { ArrowDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { ResponsiveLine } from '@nivo/line'
import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'

import { DatePicker } from '@/components/forms/DatePicker'
import { MultiSelectDropdown } from '@/components/forms/MultiSelectDropdown'
import { Button } from '@/components/ui/Button'
import useAbortControllers from '@/hooks/AbortControllers'
import {
  createAnalyticsExport,
  getAnalyticsAdvisors,
  getAnalyticsExport,
  getAnalyticsTechnicians,
  getAnalyticsTimeseries,
} from '@/services/analytics.service'
import { getDealers } from '@/services/dealers.service'
import { GlobalSettingsContext } from '@/stores/GlobalSettingsStore'
import { UserStoreContext } from '@/stores/UserStore'
import colors from '@/utils/colors'
import { joinClassNames } from '@/utils/helpers'

import {
  GRANULARITY_OPTIONS,
  GROUP_BY_OPTIONS,
  METRIC_AXIS_FORMAT,
  METRIC_LABEL,
  METRIC_OPTIONS,
  METRIC_VALUE_FORMAT,
} from './constants'
import RoDrilldownSlideout from './RoDrilldownSlideout'

// Each granularity's bucket end, given the bucket's start date (a dayjs).
const BUCKET_END = {
  daily: (start) => start,
  weekly: (start) => start.add(6, 'day'),
  monthly: (start) => start.endOf('month'),
}

// Export polling cadence + cap, so a stuck/failed worker can't leave the button
// spinning forever (~60s at 1.5s intervals).
const EXPORT_POLL_MS = 1500
const MAX_EXPORT_POLLS = 40

// Distinct line colors for grouped (per-advisor) series; cycles if exceeded.
const SERIES_COLORS = [
  colors.blue?.[800] || '#045485',
  '#3697d3',
  '#E00025',
  '#0a8a5f',
  '#b45309',
  '#7c3aed',
  '#0891b2',
  '#be185d',
]

const toOptions = (values) => (values || []).map((value) => ({ id: value, label: value }))

// Dealers come back as objects (unlike the advisor/technician name lists), and
// the `dealer` filter keys on id. Sorted by name, same as the global RO list.
const toDealerOptions = (dealers) =>
  _.orderBy(
    (dealers || []).map((dealer) => ({ id: dealer.id, label: dealer.name })),
    'label',
  )

// Detailed Data sort state: which row key, and which direction.
const SORT_ACCESSORS = {
  bucket: (row) => row.bucket,
  value: (row) => row.value,
  group: (row) => (row.group || '').toLowerCase(),
  roCount: (row) => row.roCount,
}

/**
 * SortableHeader — clickable `<th>` that toggles asc/desc on its column. The
 * arrow matches DataTable's sort affordance (a single arrow, flipped), and is
 * only fully opaque on the active column.
 */
const SortableHeader = ({ label, sortKey, sort, onSort, className }) => {
  const active = sort.key === sortKey
  return (
    <th
      className={joinClassNames('px-6 py-3', className)}
      aria-sort={active ? sort.direction : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="hover:text-charcoal-500 group flex items-center gap-1"
      >
        {label}
        <ArrowDownIcon
          className={joinClassNames(
            'h-3 w-3 transition-all',
            active ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
            active && sort.direction === 'ascending' ? 'rotate-180' : '',
          )}
          aria-hidden="true"
        />
      </button>
    </th>
  )
}

SortableHeader.propTypes = {
  label: PropTypes.string.isRequired,
  sortKey: PropTypes.oneOf(Object.keys(SORT_ACCESSORS)).isRequired,
  sort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
  className: PropTypes.string,
}

/**
 * SegmentedControl — small inline segmented toggle (granularity, group-by).
 */
const SegmentedControl = ({ options, value, onChange, ariaLabel }) => (
  <div className="bg-background flex rounded-lg p-1" role="group" aria-label={ariaLabel}>
    {options.map((option) => {
      const active = option.id === value
      return (
        <button
          key={option.id}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(option.id)}
          className={joinClassNames(
            'rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-all',
            active ? 'bg-white text-blue-800 shadow-sm' : 'text-gray hover:text-charcoal-500',
          )}
        >
          {option.label}
        </button>
      )
    })}
  </div>
)

SegmentedControl.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
}

/**
 *
 * Analytics — "Performance Explorer"
 *
 * Cross-dealer analytics page: trend a primary metric (RO volume, rework rate,
 * avg cycle time) over time, optionally grouped by advisor, with technician /
 * advisor / date filtering. Read-only; scoping enforced server-side. Behind the
 * `is_analytics_enabled` feature flag.
 */
const Analytics = observer(() => {
  const navigate = useNavigate()
  const { getSignal } = useAbortControllers()
  const { isAnalyticsEnabled, loaded: settingsLoaded } = useContext(GlobalSettingsContext)
  const { isImpersonating } = useContext(UserStoreContext)

  // Guard direct navigation when the feature flag is off (mirrors RepairOrders).
  useEffect(() => {
    if (settingsLoaded && !isAnalyticsEnabled) {
      navigate('/dashboard', { replace: true })
    }
  }, [settingsLoaded, isAnalyticsEnabled])

  // Explorer controls
  const [metric, setMetric] = useState('ro_volume')
  const [granularity, setGranularity] = useState('daily')
  const [groupBy, setGroupBy] = useState('none')
  const [startDate, setStartDate] = useState(dayjs().subtract(29, 'day').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [selectedTechnicians, setSelectedTechnicians] = useState([])
  const [selectedAdvisors, setSelectedAdvisors] = useState([])
  const [selectedDealers, setSelectedDealers] = useState([])

  // Detailed Data sort — bucket ascending matches the server's row order.
  const [sort, setSort] = useState({ key: 'bucket', direction: 'ascending' })

  // Data
  const [technicianOptions, setTechnicianOptions] = useState([])
  const [advisorOptions, setAdvisorOptions] = useState([])
  const [dealerOptions, setDealerOptions] = useState([])
  const [rows, setRows] = useState([])
  const [loadingChart, setLoadingChart] = useState(true)
  const [error, setError] = useState(null)

  // Date-range picker open state (one per picker).
  const [showStartPicker, setShowStartPicker] = useState(false)
  const [showEndPicker, setShowEndPicker] = useState(false)

  // Detailed Data drill-down ({ query, subtitle } for the clicked row, or null).
  const [drilldown, setDrilldown] = useState(null)

  // Export
  const [exporting, setExporting] = useState(false)
  const exportPoll = useRef(null)

  const formatValue = METRIC_VALUE_FORMAT[metric]
  const formatAxisValue = METRIC_AXIS_FORMAT[metric]

  // Filter dimensions shared by every request (technician → line items).
  const dimensionQuery = useMemo(() => {
    const parts = []
    selectedTechnicians.forEach((t) =>
      parts.push(`line_items__technician=${encodeURIComponent(t.id)}`),
    )
    selectedAdvisors.forEach((a) => parts.push(`advisor=${encodeURIComponent(a.id)}`))
    selectedDealers.forEach((d) => parts.push(`dealer=${encodeURIComponent(d.id)}`))
    return parts.join('&')
  }, [selectedTechnicians, selectedAdvisors, selectedDealers])

  // Full explorer query (dimensions + metric/granularity/group-by/date range).
  const explorerQuery = useMemo(() => {
    const parts = [
      `metric=${metric}`,
      `granularity=${granularity}`,
      `group_by=${groupBy}`,
      `start=${startDate}`,
      `end=${endDate}`,
    ]
    if (dimensionQuery) parts.push(dimensionQuery)
    return parts.join('&')
  }, [metric, granularity, groupBy, startDate, endDate, dimensionQuery])

  // "Filters" = the technician/advisor selections and the date range (the metric,
  // granularity, and group-by are view controls, not filters). Clear resets them
  // to the defaults; show the action only when something is actually set.
  const defaultStart = dayjs().subtract(29, 'day').format('YYYY-MM-DD')
  const defaultEnd = dayjs().format('YYYY-MM-DD')
  const filtersActive =
    selectedTechnicians.length > 0 ||
    selectedAdvisors.length > 0 ||
    selectedDealers.length > 0 ||
    startDate !== defaultStart ||
    endDate !== defaultEnd

  const clearFilters = () => {
    setSelectedTechnicians([])
    setSelectedAdvisors([])
    setSelectedDealers([])
    setStartDate(defaultStart)
    setEndDate(defaultEnd)
  }

  // Load filter options once enabled. Each is scoped server-side.
  useEffect(() => {
    if (!settingsLoaded || !isAnalyticsEnabled) return undefined
    let active = true
    const load = async () => {
      const [advisors, technicians, dealersResponse] = await Promise.all([
        getAnalyticsAdvisors(
          '',
          () => {},
          () => {},
          getSignal('opt-advisors'),
        ),
        getAnalyticsTechnicians(
          '',
          () => {},
          () => {},
          getSignal('opt-technicians'),
        ),
        // High limit so the dealer dropdown is effectively complete, mirroring
        // the global RO list's dealer filter (server-side search is a follow-up
        // if scale demands it).
        getDealers(
          '/dealers/?limit=1000',
          () => {},
          () => {},
          getSignal('opt-dealers'),
        ),
      ])
      if (!active) return
      if (advisors) setAdvisorOptions(toOptions(advisors))
      if (technicians) setTechnicianOptions(toOptions(technicians))
      if (dealersResponse) setDealerOptions(toDealerOptions(dealersResponse.results))
    }
    load()
    return () => {
      active = false
    }
  }, [settingsLoaded, isAnalyticsEnabled])

  // Load the time series when any explorer param changes.
  useEffect(() => {
    if (!settingsLoaded || !isAnalyticsEnabled) return
    // Drop stale rows/error before refetching so the chart and table never show
    // the previous metric/grouping's data against the new controls.
    setRows([])
    setError(null)
    const load = async () => {
      const response = await getAnalyticsTimeseries(
        explorerQuery,
        setLoadingChart,
        setError,
        getSignal('timeseries'),
      )
      if (response) setRows(response.data)
    }
    load()
  }, [settingsLoaded, isAnalyticsEnabled, explorerQuery])

  // Clean up any in-flight export poll on unmount.
  useEffect(
    () => () => {
      if (exportPoll.current) clearInterval(exportPoll.current)
    },
    [],
  )

  // Build nivo series: one "Overall" line, or one line per advisor when grouped.
  const series = useMemo(() => {
    if (groupBy === 'advisor') {
      const grouped = _.groupBy(rows, (r) => r.group || 'Unassigned')
      return Object.entries(grouped).map(([id, groupRows]) => ({
        id,
        data: groupRows.map((r) => ({ x: r.bucket, y: r.value })),
      }))
    }
    return [{ id: METRIC_LABEL[metric], data: rows.map((r) => ({ x: r.bucket, y: r.value })) }]
  }, [rows, groupBy, metric])

  // Distinct bucket labels in order — drives daily x-axis tick thinning. Built
  // from rows (not series[0]) so it's correct when grouped by advisor, where the
  // first series doesn't contain every bucket.
  const axisBuckets = useMemo(() => [...new Set(rows.map((r) => r.bucket))], [rows])

  const hasData = rows.length > 0

  // Detailed Data is sorted client-side: the full series is already in memory
  // (it's what the chart draws), so there's nothing to round-trip for.
  const sortedRows = useMemo(
    () =>
      _.orderBy(rows, SORT_ACCESSORS[sort.key], sort.direction === 'ascending' ? 'asc' : 'desc'),
    [rows, sort],
  )

  // Click a header to sort by it; click the active one to flip direction. New
  // columns start ascending — chronological for Date, which is the ask.
  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'ascending' ? 'descending' : 'ascending' }
        : { key, direction: 'ascending' },
    )

  // Grouping controls whether the Advisor column exists; drop the sort back to
  // Date rather than leaving it keyed on a column that's no longer rendered.
  useEffect(() => {
    if (groupBy !== 'advisor' && sort.key === 'group') {
      setSort({ key: 'bucket', direction: 'ascending' })
    }
  }, [groupBy])

  // Open the drill-down for a Detailed Data row: list the ROs behind that bucket.
  // The bucket's population is its date range ∩ the explorer window, filtered on
  // the metric's timestamp (received_at for volume, resolved_at otherwise) plus
  // the row's advisor (when grouped) and the active technician/advisor filters.
  const openDrilldown = (row) => {
    let bucketStart = dayjs(row.bucket)
    let bucketEnd = BUCKET_END[granularity](bucketStart)
    if (bucketStart.isBefore(dayjs(startDate))) bucketStart = dayjs(startDate)
    if (bucketEnd.isAfter(dayjs(endDate))) bucketEnd = dayjs(endDate)

    const dateField = metric === 'ro_volume' ? 'received_at' : 'resolved_at'
    const parts = [
      `${dateField}__gte=${bucketStart.format('YYYY-MM-DD')}`,
      `${dateField}__lte=${bucketEnd.format('YYYY-MM-DD')}`,
    ]
    if (groupBy === 'advisor') {
      if (row.group) parts.push(`advisor=${encodeURIComponent(row.group)}`)
    } else {
      selectedAdvisors.forEach((a) => parts.push(`advisor=${encodeURIComponent(a.id)}`))
    }
    selectedTechnicians.forEach((t) =>
      parts.push(`line_items__technician=${encodeURIComponent(t.id)}`),
    )
    selectedDealers.forEach((d) => parts.push(`dealer=${encodeURIComponent(d.id)}`))

    const dateLabel =
      granularity === 'daily'
        ? bucketStart.format('MMM D, YYYY')
        : `${bucketStart.format('MMM D')} – ${bucketEnd.format('MMM D, YYYY')}`
    const subtitle = [dateLabel, row.group].filter(Boolean).join(' · ')

    setDrilldown({ query: parts.join('&'), subtitle })
  }

  const startExport = async () => {
    setError(null)
    setExporting(true)
    const created = await createAnalyticsExport(explorerQuery, setError)
    if (!created) {
      setExporting(false)
      return
    }

    // Clear any prior poll, then poll until the worker finishes — capped so a
    // network/server failure can't leave the button stuck on "Exporting…".
    if (exportPoll.current) clearInterval(exportPoll.current)
    let attempts = 0
    let inFlight = false // skip a tick if the prior request is still pending
    let stopped = false // ensure we stop (and open the file) exactly once
    const stop = () => {
      stopped = true
      clearInterval(exportPoll.current)
      exportPoll.current = null
      setExporting(false)
    }
    exportPoll.current = setInterval(async () => {
      if (inFlight || stopped) return
      inFlight = true
      attempts += 1
      try {
        const status = await getAnalyticsExport(created.id, setError)
        if (stopped) return
        if (status?.status === 'Exported') {
          stop()
          if (status.file) window.open(status.file, '_blank', 'noopener')
        } else if (status?.status === 'Failed') {
          stop()
          setError('Export failed. Please try again.')
        } else if (attempts >= MAX_EXPORT_POLLS) {
          stop()
          setError('Export timed out. Please try again.')
        }
      } finally {
        inFlight = false
      }
    }, EXPORT_POLL_MS)
  }

  return (
    <div className="flex flex-col">
      <Helmet>
        <title>Analytics</title>
      </Helmet>

      <h5 className="mb-0">Analytics</h5>
      <p className="text-charcoal-400 text-sm">
        Analyze trends across technicians and advisors with granular filtering.
      </p>

      <div className="mb-4 flex flex-col">
        <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-end lg:justify-end">
          <div className="flex flex-wrap items-end gap-4">
            <SegmentedControl
              options={GRANULARITY_OPTIONS}
              value={granularity}
              onChange={setGranularity}
              ariaLabel="Granularity"
            />
            <div className="flex items-end gap-2">
              <DatePicker
                name="analytics-start"
                label="Start Date"
                value={dayjs(startDate).format('MM/DD/YYYY')}
                onChange={(val) => setStartDate(dayjs(val).format('YYYY-MM-DD'))}
                show={showStartPicker}
                setShow={setShowStartPicker}
              />
              <DatePicker
                name="analytics-end"
                label="End Date"
                value={dayjs(endDate).format('MM/DD/YYYY')}
                onChange={(val) => setEndDate(dayjs(val).format('YYYY-MM-DD'))}
                show={showEndPicker}
                setShow={setShowEndPicker}
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border-red/20 bg-red/5 text-red mb-4 rounded-lg border px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Sidebar filters */}
        <div className="border-gray-light h-fit space-y-6 rounded-xl border bg-white p-6 shadow-sm">
          <div className="border-gray-light flex items-center justify-between border-b pb-3">
            <span className="text-charcoal-500 text-xs font-bold uppercase tracking-widest">
              Filters
            </span>
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-semibold text-blue-800 hover:text-blue-900"
              >
                Clear all
              </button>
            )}
          </div>

          <MultiSelectDropdown
            id="metric-filter"
            label="Primary Metric"
            singleSelect
            options={METRIC_OPTIONS}
            value={METRIC_OPTIONS.filter((o) => o.id === metric)}
            onChange={(selected) => {
              // A primary metric must always be set — ignore deselection.
              if (selected.length) setMetric(selected[0].id)
            }}
          />

          {/* Hidden for users scoped to a single dealer — filtering to the only
              dealer they can see is a no-op control. */}
          {dealerOptions.length > 1 && (
            <MultiSelectDropdown
              id="dealer-filter"
              label="Dealer"
              search
              placeholder="All Dealers"
              options={dealerOptions}
              value={selectedDealers}
              onChange={setSelectedDealers}
            />
          )}

          <MultiSelectDropdown
            id="technician-filter"
            label="Technician"
            search
            placeholder="All Technicians"
            options={technicianOptions}
            value={selectedTechnicians}
            onChange={setSelectedTechnicians}
          />

          <MultiSelectDropdown
            id="advisor-filter"
            label="Service Advisor"
            search
            placeholder="All Advisors"
            options={advisorOptions}
            value={selectedAdvisors}
            onChange={setSelectedAdvisors}
          />
        </div>

        {/* Main content */}
        <div className="space-y-6">
          {/* Chart */}
          <div className="border-gray-light rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-charcoal-500 text-lg font-bold">Metric Analysis</h3>
              <div className="flex items-center gap-3">
                <span className="text-gray text-[10px] font-bold uppercase tracking-widest">
                  Group Trend By
                </span>
                <SegmentedControl
                  options={GROUP_BY_OPTIONS}
                  value={groupBy}
                  onChange={setGroupBy}
                  ariaLabel="Group trend by"
                />
              </div>
            </div>

            <div className="h-[400px] w-full">
              {!hasData ? (
                <div className="text-gray flex h-full flex-col items-center justify-center gap-1">
                  <span className="text-sm font-medium">
                    {loadingChart ? 'Loading…' : 'No data available for this period'}
                  </span>
                  {!loadingChart && (
                    <span className="text-charcoal-300 text-xs">
                      Try a different date range or filters.
                    </span>
                  )}
                </div>
              ) : (
                <ResponsiveLine
                  data={series}
                  margin={{ top: 10, right: 20, bottom: 80, left: 50 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 0, max: 'auto' }}
                  yFormat={(v) => formatValue(v)}
                  axisBottom={{
                    tickRotation: -45,
                    tickSize: 0,
                    tickPadding: 10,
                    format: (v) => {
                      const formatted = dayjs(v).format('MMM D')
                      if (granularity === 'daily' && axisBuckets.length > 14) {
                        const idx = axisBuckets.indexOf(v)
                        return idx % Math.ceil(axisBuckets.length / 10) === 0 ? formatted : ''
                      }
                      return formatted
                    },
                  }}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 10,
                    format: (v) => formatAxisValue(v),
                    tickValues: 5,
                  }}
                  pointSize={groupBy === 'advisor' ? 0 : 6}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  colors={SERIES_COLORS}
                  enableArea={false}
                  useMesh
                  legends={
                    groupBy === 'advisor'
                      ? [
                          {
                            anchor: 'bottom',
                            direction: 'row',
                            translateY: 70,
                            itemWidth: 110,
                            itemHeight: 18,
                            symbolSize: 10,
                            symbolShape: 'circle',
                          },
                        ]
                      : []
                  }
                  theme={{
                    axis: {
                      ticks: { text: { fill: colors.charcoal?.[400] || '#888', fontSize: 11 } },
                    },
                    grid: { line: { stroke: '#f0f0f0' } },
                  }}
                />
              )}
            </div>
          </div>

          {/* Detailed data table */}
          <div className="border-gray-light overflow-hidden rounded-xl border bg-white shadow-sm">
            <div className="bg-background border-gray-light flex items-center justify-between border-b px-6 py-3">
              <h4 className="text-charcoal-500 text-xs font-bold uppercase tracking-widest">
                Detailed Data
              </h4>
              <Button
                label={exporting ? 'Exporting…' : 'Export'}
                icon={<ArrowDownTrayIcon className="h-4 w-4" />}
                onClick={startExport}
                loading={exporting}
                disabled={exporting || !hasData || isImpersonating}
                background="bg-white"
                size="sm"
                outlined
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-background text-gray border-gray-light border-b text-[10px] font-bold uppercase tracking-widest">
                  <tr>
                    <SortableHeader
                      label="Date"
                      sortKey="bucket"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortableHeader
                      label={METRIC_LABEL[metric]}
                      sortKey="value"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    {groupBy === 'advisor' && (
                      <SortableHeader
                        label="Service Advisor"
                        sortKey="group"
                        sort={sort}
                        onSort={toggleSort}
                      />
                    )}
                    <SortableHeader
                      label="RO Count"
                      sortKey="roCount"
                      sort={sort}
                      onSort={toggleSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-gray-light divide-y">
                  {!hasData ? (
                    <tr>
                      <td
                        className="text-gray p-6 text-center"
                        colSpan={groupBy === 'advisor' ? 4 : 3}
                      >
                        {loadingChart ? 'Loading…' : 'No data for the selected range.'}
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((row) => (
                      <tr key={`${row.bucket}-${row.group || 'all'}`} className="hover:bg-gray-50">
                        <td className="px-6 py-3">
                          <button
                            type="button"
                            onClick={() => openDrilldown(row)}
                            className="font-medium text-blue-800 hover:text-blue-900 hover:underline"
                            aria-label={`View repair orders for ${dayjs(row.bucket).format(
                              'MMM D, YYYY',
                            )}`}
                          >
                            {dayjs(row.bucket).format('MMM D, YYYY')}
                          </button>
                        </td>
                        <td className="px-6 py-3 font-medium text-blue-800">
                          {formatValue(row.value)}
                        </td>
                        {groupBy === 'advisor' && (
                          <td className="text-gray px-6 py-3">{row.group || 'Unassigned'}</td>
                        )}
                        <td className="text-gray px-6 py-3">{row.roCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <RoDrilldownSlideout
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        query={drilldown?.query}
        title="Repair Orders"
        subtitle={drilldown?.subtitle}
      />
    </div>
  )
})

export default Analytics
